<script lang="ts">
  import { api } from '$shared/api';
  import { markAllRead, markRead, fetchUnreadCount } from '$lib/stores/notifications.svelte';
  import { navigate } from '$lib/router.svelte';
  import { notifMessage, notifLink, notifIcon, timeAgo, type NotifItem } from '$lib/utils/notifications';
  import Avatar from '$shared/Avatar.svelte';

  let { open = $bindable(false) }: { open: boolean } = $props();

  let notifications = $state<NotifItem[]>([]);
  let loading = $state(false);
  let loaded = $state(false);

  $effect(() => {
    if (open && !loaded) {
      loadNotifications();
    }
  });

  async function loadNotifications() {
    loading = true;
    try {
      const data = await api.get<NotifItem[]>('/notifications/grouped?per-page=8');
      notifications = Array.isArray(data) ? data : [];
      loaded = true;
    } catch {
      notifications = [];
    }
    loading = false;
  }

  async function handleMarkAllRead() {
    await markAllRead();
    notifications = notifications.map((n) => ({ ...n, is_read: true }));
  }

  function handleViewAll() {
    open = false;
    navigate('/account/notifications/list');
  }

  async function handleClick(notif: NotifItem) {
    open = false;
    if (!notif.is_read) {
      await markRead(notif.id);
      const idx = notifications.findIndex((n) => n.id === notif.id);
      if (idx >= 0) notifications[idx] = { ...notifications[idx], is_read: true };
    }
    const link = notifLink(notif);
    if (link) navigate(link);
  }

  function close() {
    open = false;
  }

  // Refresh when re-opened
  $effect(() => {
    if (open && loaded) {
      loadNotifications();
      fetchUnreadCount();
    }
  });
</script>

{#if open}
  <!-- svelte-ignore a11y_click_events_have_key_events -->
  <div class="notif-overlay" role="button" tabindex="-1" onclick={close}></div>
  <div
    class="notif-dropdown"
    role="dialog"
    aria-label="Notifications"
    tabindex="-1"
    onkeydown={(e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    }}
  >
    <div class="notif-header">
      <span class="notif-title">Notifications</span>
      <div class="notif-header-actions">
        <button class="notif-mark-read" onclick={handleMarkAllRead}>Mark all read</button>
        <a
          href="#/account/notifications"
          class="notif-settings"
          title="Notification settings"
          onclick={() => (open = false)}
        >
          <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
            />
            <circle cx="12" cy="12" r="3" />
          </svg>
        </a>
      </div>
    </div>

    {#if loading && !loaded}
      <div class="notif-loading">Loading...</div>
    {:else if notifications.length === 0}
      <div class="notif-empty">No notifications yet</div>
    {:else}
      <div class="notif-list">
        {#each notifications as notif}
          <button class="notif-item" class:notif-item--unread={!notif.is_read} onclick={() => handleClick(notif)}>
            {#if notif.grouped && notif.actors && notif.actors.length > 1}
              <div class="notif-avatars">
                {#each notif.actors.slice(0, 3) as actor}
                  <Avatar username={actor.username} avatarUrl={actor.avatar_url} size="xs" />
                {/each}
              </div>
            {:else if notif.actor}
              <Avatar username={notif.actor.username} avatarUrl={notif.actor.avatar_url} size="xs" />
            {:else}
              <span class="notif-icon">{notifIcon(notif.type)}</span>
            {/if}
            <div class="notif-body">
              <span class="notif-msg">{notifMessage(notif)}</span>
              <span class="notif-time">{timeAgo(notif.created_at)}</span>
            </div>
            {#if !notif.is_read}
              <span class="notif-dot"></span>
            {/if}
          </button>
        {/each}
      </div>
    {/if}

    <div class="notif-footer">
      <button class="notif-view-all" onclick={handleViewAll}>View all notifications</button>
    </div>
  </div>
{/if}

<style>
  .notif-overlay {
    position: fixed;
    inset: 0;
    z-index: 40;
  }
  .notif-dropdown {
    position: absolute;
    right: 0;
    top: 100%;
    margin-top: 0.5rem;
    z-index: 50;
    width: 22rem;
    max-height: 28rem;
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .notif-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.75rem 1rem;
    border-bottom: 1px solid var(--border);
  }
  .notif-title {
    font-weight: 700;
    font-size: 0.875rem;
  }
  .notif-header-actions {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
  .notif-mark-read {
    font-size: 0.75rem;
    color: var(--cyan);
    background: none;
    border: none;
    cursor: pointer;
    padding: 0;
    font-weight: 500;
  }
  .notif-mark-read:hover {
    text-decoration: underline;
  }
  .notif-settings {
    color: var(--text-muted);
    display: flex;
    align-items: center;
    padding: 0.125rem;
    border-radius: 4px;
    transition: color 0.15s;
  }
  .notif-settings:hover {
    color: var(--cyan);
  }

  .notif-loading,
  .notif-empty {
    padding: 2rem 1rem;
    text-align: center;
    color: var(--text-muted);
    font-size: 0.875rem;
  }

  .notif-list {
    overflow-y: auto;
    max-height: 20rem;
  }

  .notif-item {
    display: flex;
    align-items: center;
    gap: 0.625rem;
    width: 100%;
    padding: 0.625rem 1rem;
    background: none;
    border: none;
    border-bottom: 1px solid var(--border);
    cursor: pointer;
    text-align: left;
    color: var(--text);
    transition: background 0.15s;
  }
  .notif-item:last-child {
    border-bottom: none;
  }
  .notif-item:hover {
    background: var(--surface-2);
  }
  .notif-item--unread {
    background: rgba(0, 210, 211, 0.04);
  }

  .notif-icon {
    font-size: 1rem;
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .notif-avatars {
    display: flex;
    position: relative;
    width: fit-content;
    flex-shrink: 0;
  }
  .notif-avatars :global(.avatar) {
    width: 28px;
    height: 28px;
    border: 2px solid var(--surface);
    margin-left: -8px;
  }
  .notif-avatars :global(.avatar:first-child) {
    margin-left: 0;
  }

  .notif-body {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 0.125rem;
  }
  .notif-msg {
    font-size: 0.8125rem;
    line-height: 1.3;
    display: -webkit-box;
    line-clamp: 2;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  .notif-time {
    font-size: 0.6875rem;
    color: var(--text-muted);
  }

  .notif-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--cyan);
    flex-shrink: 0;
  }

  .notif-footer {
    border-top: 1px solid var(--border);
    padding: 0.5rem 1rem;
    text-align: center;
  }
  .notif-view-all {
    font-size: 0.8125rem;
    color: var(--cyan);
    background: none;
    border: none;
    cursor: pointer;
    padding: 0.25rem 0;
    font-weight: 600;
  }
  .notif-view-all:hover {
    text-decoration: underline;
  }

  @media (max-width: 480px) {
    .notif-dropdown {
      position: fixed;
      left: 0.5rem;
      right: 0.5rem;
      top: 3.5rem;
      width: auto;
    }
  }
</style>
